import React, { useMemo, useState } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Chip, Stack, Typography } from '@mui/material';

import Spinner from '~/components/Common/Spinner';
import DebugModal from '~/components/DebugModal/DebugModal';

import { useGetOrder } from '~/hooks/apis/orders/useGetOrder';
import { useGetOrderPayments } from '~/hooks/apis/payments/useGetOrderPayments';

import { getAllItemsMap } from '~/utils/order';

import PromoSimulationResults from './PromoSimulationResults';
import { SimulationRow } from './multiSimulationResults';
import { SimulationWarning, buildSimulationRows } from './simulationResults';

export default function PromoSimulationMultiResult({ row }: { row: SimulationRow }) {
  const [expanded, setExpanded] = useState(false);
  const orderQuery = useGetOrder(row.orderId, { enabled: expanded });
  const paymentsQuery = useGetOrderPayments(row.orderId, expanded);
  const order = useMemo(
    () =>
      orderQuery.data?.result
        ? {
            ...orderQuery.data.result,
            payments: paymentsQuery.data?.result ?? [],
          }
        : null,
    [orderQuery.data, paymentsQuery.data],
  );
  const items = useMemo(() => (order ? getAllItemsMap(order) : {}), [order]);
  const warnings: Array<SimulationWarning> = [
    ...(row.error ? [{ scope: 'Promo', title: 'Calculation error', message: row.error }] : []),
    ...(orderQuery.error ? [{ scope: 'Order', title: 'Order load warning', message: orderQuery.error.message }] : []),
    ...(paymentsQuery.error
      ? [{ scope: 'Order', title: 'Payments load warning', message: paymentsQuery.error.message }]
      : []),
  ];
  const warningCount = buildSimulationRows(order, items, row.result ?? null, warnings).warningRows.length;

  return (
    <Accordion expanded={expanded} onChange={(_, next) => setExpanded(next)}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
          <Typography variant="subtitle2">Order {row.orderNumber || row.orderId}</Typography>
          <Chip
            size="small"
            label={row.calculatorStatus}
            color={row.calculatorStatus === 'error' ? 'error' : 'default'}
          />
          <Typography variant="body2">
            {row.status} · {row.total} · {row.createdAt}
          </Typography>
          {row.result && (
            <Typography variant="body2">
              {row.promoCode || 'Vouchers only'} · Promo {row.discount} · Vouchers {row.vouchers}
            </Typography>
          )}
          {warningCount > 0 && <Chip size="small" label={`${warningCount} warnings`} color="error" />}
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        {expanded && (
          <Stack spacing={2}>
            {(orderQuery.isLoading || paymentsQuery.isLoading) && <Spinner />}
            {row.calculatorStatus === 'running' && <Typography variant="body2">Calculating discounts…</Typography>}
            <PromoSimulationResults
              order={order}
              items={items}
              result={row.result ?? null}
              codeName={row.submittedCodeName}
              voucherIds={row.submittedVoucherIds}
              warnings={warnings}
            />
            <DebugModal
              title="Source order and response"
              type="generic"
              data={{ order: row.sourceOrder, response: row.debugData }}
            />
          </Stack>
        )}
      </AccordionDetails>
    </Accordion>
  );
}
